import { IOptionItem } from '@wyz/types';
import { LocaleUtils } from '@wyz/utils';
import { useMemo } from 'react';
import {
  ILanguage,
  ILanguageRecordByLetter,
  ITenantLanguageResponse,
} from '~/common/types';
import { COUNTRY_STATUS } from '~/connect/schemas';

// eslint-disable-next-line @wyz/module/no-relative-import
import BaseAlphabeticalList from './base-alphabetical-list';

type Props = {
  tags: Array<IOptionItem>;
  onAdd: (item: ILanguage) => void;
  onRemove: (item: ILanguage) => void;
  allLanguages: Array<ILanguage>;
  activeLanguagesByIsoCode: ITenantLanguageResponse['byIsoCode'];
};
const groupByLetter = (data: Array<ILanguage>) => {
  return data.reduce((acc, curr) => {
    const humanReadableLabel =
      LocaleUtils.getHumanReadableLabel({
        userLocale: curr.isoCode,
        locale: curr.isoCode,
        fallback: curr.name,
      }) ?? curr.name;
    const letter = humanReadableLabel[0].toUpperCase();
    if (!acc[letter]) {
      acc[letter] = [];
    }
    acc[letter].push(curr);
    return acc;
  }, {} as ILanguageRecordByLetter);
};

function getLanguageName(language: ILanguage): string {
  return (
    LocaleUtils.getHumanReadableLabel({
      userLocale: language.isoCode,
      locale: language.isoCode,
      fallback: language.name,
    }) ?? language.name
  );
}

export default function LanguageAlphabeticalList({
  allLanguages,
  activeLanguagesByIsoCode,
  tags,
  onAdd,
  onRemove,
}: Props) {
  const memoized: ILanguageRecordByLetter = useMemo(() => {
    return groupByLetter(allLanguages);
  }, [allLanguages]);
  return (
    <BaseAlphabeticalList.Container>
      <BaseAlphabeticalList.Letters
        getLetterKey={(letter) => letter}
        itemsByLetter={memoized}
      />
      <BaseAlphabeticalList.List
        itemsByLetter={memoized}
        getLetterKey={(letter) => letter}
        getItemIdentifier={(language) => language.isoCode}
        getItemLabel={(language) => getLanguageName(language)}
        canConfigure={false}
        onAdd={onAdd}
        isAdded={(item) =>
          activeLanguagesByIsoCode[item.isoCode]?.status ===
          COUNTRY_STATUS.ACTIVE
        }
        onRemove={onRemove}
        preFilterer={(item) => {
          if (!tags.length) return true;

          const values = tags.map((tag) => tag.label);
          for (let index = 0; index < values.length; index++) {
            const element = values[index];
            if (
              getLanguageName(item)
                .trim()
                .toLowerCase()
                .includes(element.trim().toLowerCase())
            ) {
              return true;
            }
          }
          return false;
        }}
      />
    </BaseAlphabeticalList.Container>
  );
}
